import React, { useCallback, useEffect, useState, useRef, Fragment, Component } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents, Circle, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import { PackageHoverTooltip } from './PackageHoverTooltip';
import { PropertyHoverTooltip } from './PropertyHoverTooltip';
import { EmergencyHoverTooltip, EmergencyIntervention } from './EmergencyHoverTooltip';
// Extended interfaces to match OpportunitiesPage data
interface Property {
  id: number;
  lat: number;
  lng: number;
  address: string;
  isIncluded?: boolean;
  // New fields for tooltip
  mq: number;
  rooms: number;
  bathrooms: number;
  timeWindow: string;
  tags: string[];
}
interface PackageData {
  id: number;
  lat: number;
  lng: number;
  zoneName: string;
  count: number;
  properties: Property[];
  // New fields for tooltip
  name: string;
  baseEarnings: number;
}
interface PackageMapProps {
  packages: PackageData[];
  activePackageId: number | null;
  onPackageSelect: (id: number) => void;
  isEditing: boolean;
  editedProperties?: Property[];
  nearbyProperties?: Property[];
  onToggleProperty?: (property: Property) => void;
  emergencyInterventions?: EmergencyIntervention[];
  onEmergencyClick?: (id: number) => void;
  selectedEmergencyId?: number | null;
  onEmergencyPositionUpdate?: (pos: {
    x: number;
    y: number;
  } | null) => void;
}
// Threshold: above this zoom level, split package dot into individual property markers
const SPLIT_ZOOM_THRESHOLD = 15;
// --- Tile Pre-caching Utility ---
const TILE_URL_TEMPLATE = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';
const TILE_SUBDOMAINS = ['a', 'b', 'c', 'd'];
// Convert lat/lng to tile coordinates at a given zoom
function latLngToTile(lat: number, lng: number, zoom: number): {
  x: number;
  y: number;
} {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return {
    x,
    y
  };
}
// Generate tile URLs for a 3x3 grid around a center tile
function getTileUrls(centerLat: number, centerLng: number, zoom: number): string[] {
  const {
    x: cx,
    y: cy
  } = latLngToTile(centerLat, centerLng, zoom);
  const urls: string[] = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const x = cx + dx;
      const y = cy + dy;
      // Pick a subdomain based on tile coords for distribution
      const s = TILE_SUBDOMAINS[(x + y) % TILE_SUBDOMAINS.length];
      urls.push(TILE_URL_TEMPLATE.replace('{s}', s).replace('{z}', String(zoom)).replace('{x}', String(x)).replace('{y}', String(y)));
    }
  }
  return urls;
}
// Pre-cache tiles for all package zones at detail zoom levels
function TilePreloader({
  packages
}: {
  packages: PackageData[];
}) {
  const preloadedRef = useRef(false);
  useEffect(() => {
    if (preloadedRef.current || packages.length === 0) return;
    preloadedRef.current = true;
    // Collect all tile URLs to pre-fetch
    const allUrls = new Set<string>();
    const zoomLevels = [15, 16, 17];
    for (const pkg of packages) {
      // Pre-cache around the package center
      for (const z of zoomLevels) {
        getTileUrls(pkg.lat, pkg.lng, z).forEach(url => allUrls.add(url));
      }
      // Also pre-cache around each property location for tighter zoom
      for (const prop of pkg.properties) {
        for (const z of zoomLevels) {
          getTileUrls(prop.lat, prop.lng, z).forEach(url => allUrls.add(url));
        }
      }
    }
    const urlArray = Array.from(allUrls);
    let index = 0;
    const BATCH_SIZE = 6; // Load 6 tiles at a time (browser connection limit per domain)
    function loadBatch() {
      const batch = urlArray.slice(index, index + BATCH_SIZE);
      if (batch.length === 0) return;
      batch.forEach(url => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
      });
      index += BATCH_SIZE;
      // Use requestIdleCallback if available, otherwise setTimeout
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(() => loadBatch(), {
          timeout: 2000
        });
      } else {
        setTimeout(loadBatch, 100);
      }
    }
    // Start pre-loading after a short delay to not compete with initial render
    const startTimer = setTimeout(loadBatch, 500);
    return () => clearTimeout(startTimer);
  }, [packages]);
  return null;
}
// Force map to recalculate size after mount and on resize
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  return null;
}
// Track current zoom level
function ZoomTracker({
  onZoomChange
}: {
  onZoomChange: (zoom: number) => void;
}) {
  const map = useMapEvents({
    zoomend: () => {
      onZoomChange(map.getZoom());
    }
  });
  useEffect(() => {
    onZoomChange(map.getZoom());
  }, []);
  return null;
}
// Track whether the map is currently animating (zoom/pan)
function MapMovementTracker({
  onMovingChange
}: {
  onMovingChange: (moving: boolean) => void;
}) {
  const map = useMapEvents({
    movestart: () => onMovingChange(true),
    zoomstart: () => onMovingChange(true),
    moveend: () => onMovingChange(false),
    zoomend: () => onMovingChange(false)
  });
  return null;
}
// Component to handle map fly-to animations
function MapController({
  center,
  zoom,
  activePackage
}: {
  center: [number, number];
  zoom: number;
  activePackage: PackageData | null;
}) {
  const map = useMap();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      if (activePackage && activePackage.properties.length > 0) {
        const bounds = computePackageBounds(activePackage);
        map.fitBounds(bounds, {
          padding: [60, 60],
          paddingTopLeft: [420, 60],
          maxZoom: 18,
          animate: false
        });
      } else {
        map.setView(center, zoom);
      }
      return;
    }
    if (activePackage && activePackage.properties.length > 0) {
      const bounds = computePackageBounds(activePackage);
      map.flyToBounds(bounds, {
        padding: [60, 60],
        paddingTopLeft: [420, 60],
        maxZoom: 18,
        duration: 1.5
      });
    } else {
      map.flyTo(center, zoom, {
        duration: 1.2
      });
    }
  }, [center[0], center[1], zoom, activePackage?.id]);
  return null;
}
// Compute LatLngBounds from a package's properties with padding
function computePackageBounds(pkg: PackageData): L.LatLngBounds {
  const lats = pkg.properties.map(p => p.lat);
  const lngs = pkg.properties.map(p => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  // Add 40% padding around the bounds so the circle doesn't touch edges
  const latPad = Math.max((maxLat - minLat) * 0.4, 0.002);
  const lngPad = Math.max((maxLng - minLng) * 0.4, 0.002);
  return L.latLngBounds([minLat - latPad, minLng - lngPad], [maxLat + latPad, maxLng + lngPad]);
}
// Helper to update tooltip position when map moves/zooms
function TooltipPositionUpdater({
  hoveredPackage,
  hoveredEmergency,
  hoveredProperty,
  onPositionUpdate
}: {
  hoveredPackage: PackageData | null;
  hoveredEmergency: EmergencyIntervention | null;
  hoveredProperty: Property | null;
  onPositionUpdate: (pos: {
    x: number;
    y: number;
  }) => void;
}) {
  const map = useMap();
  const updatePosition = useCallback(() => {
    if (hoveredPackage) {
      const point = map.latLngToContainerPoint([hoveredPackage.lat, hoveredPackage.lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    } else if (hoveredEmergency) {
      const point = map.latLngToContainerPoint([hoveredEmergency.lat, hoveredEmergency.lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    } else if (hoveredProperty) {
      const point = map.latLngToContainerPoint([hoveredProperty.lat, hoveredProperty.lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    }
  }, [map, hoveredPackage, hoveredEmergency, hoveredProperty, onPositionUpdate]);
  useEffect(() => {
    updatePosition();
  }, [updatePosition]);
  useMapEvents({
    move: updatePosition,
    zoom: updatePosition
  });
  return null;
}
// Helper to track the selected emergency position
function SelectedEmergencyTracker({
  selectedEmergencyId,
  emergencyInterventions,
  onPositionUpdate
}: {
  selectedEmergencyId: number | null;
  emergencyInterventions: EmergencyIntervention[];
  onPositionUpdate: (pos: {
    x: number;
    y: number;
  } | null) => void;
}) {
  const map = useMap();
  const updatePosition = useCallback(() => {
    if (!selectedEmergencyId) {
      onPositionUpdate(null);
      return;
    }
    const emergency = emergencyInterventions.find(e => e.id === selectedEmergencyId);
    if (emergency) {
      const point = map.latLngToContainerPoint([emergency.lat, emergency.lng]);
      onPositionUpdate({
        x: point.x,
        y: point.y
      });
    } else {
      onPositionUpdate(null);
    }
  }, [map, selectedEmergencyId, emergencyInterventions, onPositionUpdate]);
  useEffect(() => {
    updatePosition();
  }, [updatePosition]);
  useMapEvents({
    move: updatePosition,
    zoom: updatePosition
  });
  return null;
}
// --- Icon Factories (all inline styles, no Tailwind) ---
// Zone marker: shows package count (used when zoomed out)
function createZoneIcon(count: number, isActive: boolean): L.DivIcon {
  const size = isActive ? 44 : 34;
  const bg = isActive ? '#2E8C7E' : '#ffffff';
  const color = isActive ? '#ffffff' : '#1E365C';
  const border = isActive ? '4px solid rgba(46,140,126,0.25)' : '2px solid #d1d5db';
  const shadow = isActive ? '0 4px 12px rgba(46,140,126,0.35)' : '0 2px 6px rgba(0,0,0,0.12)';
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: ${bg}; color: ${color};
      border: ${border}; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 14px; font-family: Inter, sans-serif;
      box-shadow: ${shadow}; cursor: pointer;
    ">${count}</div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Individual property dot: small teal circle (used when zoomed in on active package)
function createPropertyDotIcon(index: number): L.DivIcon {
  const size = 28;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: #2E8C7E; color: #ffffff;
      border: 2px solid #ffffff;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 11px; font-family: Inter, sans-serif;
      box-shadow: 0 2px 8px rgba(46,140,126,0.4);
      cursor: pointer;
    ">${index}</div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Edit mode: property marker with +/- badge
function createEditPropertyIcon(type: 'included' | 'available'): L.DivIcon {
  const isIncluded = type === 'included';
  const markerBg = isIncluded ? '#2E8C7E' : '#ffffff';
  const markerColor = isIncluded ? '#ffffff' : '#9ca3af';
  const markerBorder = isIncluded ? '2px solid #ffffff' : '2px dashed #d1d5db';
  const badgeBg = isIncluded ? '#ef4444' : '#22c55e';
  const badgeIcon = isIncluded ? '<line x1="5" y1="9" x2="13" y2="9" stroke="white" stroke-width="2.5" stroke-linecap="round"/>' : '<line x1="9" y1="5" x2="9" y2="13" stroke="white" stroke-width="2.5" stroke-linecap="round"/><line x1="5" y1="9" x2="13" y2="9" stroke="white" stroke-width="2.5" stroke-linecap="round"/>';
  const opacity = isIncluded ? '1' : '0.75';
  const html = `
    <div style="position: relative; opacity: ${opacity};">
      <div style="
        width: 32px; height: 32px;
        background: ${markerBg}; color: ${markerColor};
        border: ${markerBorder}; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15); cursor: pointer;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${markerColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
      <div class="jiggle" style="
        position: absolute; top: -4px; right: -4px;
        width: 20px; height: 20px;
        background: ${badgeBg}; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 1px 4px rgba(0,0,0,0.2);
      ">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">${badgeIcon}</svg>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16]
  });
}
// HQ Marker Icon
function createHeadquarterIcon(): L.DivIcon {
  const size = 40;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: #1E365C; color: #ffffff;
        border: 3px solid #ffffff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 12px rgba(30,54,92,0.3);
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
          <path d="M9 22v-4h6v4"></path>
          <path d="M8 6h.01"></path>
          <path d="M16 6h.01"></path>
          <path d="M8 10h.01"></path>
          <path d="M16 10h.01"></path>
          <path d="M8 14h.01"></path>
          <path d="M16 14h.01"></path>
        </svg>
      </div>
      <div style="
        position: absolute;
        bottom: -18px;
        left: 50%;
        transform: translateX(-50%);
        background: #ffffff;
        color: #1E365C;
        padding: 2px 6px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 700;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        white-space: nowrap;
      ">Sede</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Emergency Marker Icon
function createEmergencyIcon(): L.DivIcon {
  const size = 34;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div class="pulse-ring"></div>
      <div style="
        position: relative;
        width: ${size}px; height: ${size}px;
        background: #ef4444; color: #ffffff;
        border: 2px solid #ffffff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-weight: 800; font-size: 18px; font-family: Inter, sans-serif;
        box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        cursor: pointer;
        z-index: 10;
      ">!</div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// --- Utility: compute bounding circle for a set of properties ---
function computeBoundingCircle(properties: Property[], centerLat: number, centerLng: number): number {
  if (properties.length === 0) return 200;
  // Approximate distance in meters between two lat/lng points
  const toRad = (deg: number) => deg * Math.PI / 180;
  let maxDist = 0;
  for (const p of properties) {
    const dLat = toRad(p.lat - centerLat);
    const dLng = toRad(p.lng - centerLng);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(centerLat)) * Math.cos(toRad(p.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = 6371000 * c; // meters
    if (dist > maxDist) maxDist = dist;
  }
  // Add 30% padding, minimum 150m
  return Math.max(maxDist * 1.3, 150);
}
export function PackageMap({
  packages,
  activePackageId,
  onPackageSelect,
  isEditing,
  editedProperties = [],
  nearbyProperties = [],
  onToggleProperty,
  emergencyInterventions = [],
  onEmergencyClick,
  selectedEmergencyId,
  onEmergencyPositionUpdate
}: PackageMapProps) {
  const defaultCenter: [number, number] = [41.9028, 12.4964];
  const [currentZoom, setCurrentZoom] = useState(12);
  const activePackage = packages.find(p => p.id === activePackageId);
  // Track map animation state — hide circles during zoom/pan
  const [isMapMoving, setIsMapMoving] = useState(false);
  // Hover state for tooltips
  const [hoveredPackage, setHoveredPackage] = useState<PackageData | null>(null);
  const [hoveredEmergency, setHoveredEmergency] = useState<EmergencyIntervention | null>(null);
  const [hoveredProperty, setHoveredProperty] = useState<{
    property: Property;
    index: number;
  } | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // When a package is selected, zoom in close enough to see properties
  const center = activePackage ? [activePackage.lat, activePackage.lng] as [number, number] : defaultCenter;
  const targetZoom = activePackage ? 16 : 12;
  // Determine if we should show split view (individual property dots)
  const showSplitView = currentZoom >= SPLIT_ZOOM_THRESHOLD && !isEditing;
  const handleZoomChange = useCallback((zoom: number) => {
    setCurrentZoom(zoom);
  }, []);
  // Hover handlers for Packages
  const handleMarkerMouseOver = (pkg: PackageData) => {
    if (isEditing) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    // Clear emergency hover if any
    setHoveredEmergency(null);
    // Small delay to prevent flickering
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPackage(pkg);
    }, 150);
  };
  const handleMarkerMouseOut = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredPackage(null);
  };
  // Hover handlers for Emergency Interventions
  const handleEmergencyMouseOver = (intervention: EmergencyIntervention) => {
    if (isEditing) return;
    // Don't show hover tooltip if this emergency is already selected (panel open)
    if (selectedEmergencyId === intervention.id) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    // Clear package hover if any
    setHoveredPackage(null);
    // Small delay to prevent flickering
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredEmergency(intervention);
    }, 150);
  };
  const handleEmergencyMouseOut = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setHoveredEmergency(null);
  };
  return <div style={{
    height: '100%',
    width: '100%',
    position: 'relative'
  }} className="overflow-hidden" data-id="element-1564">
      <MapContainer center={defaultCenter} zoom={12} scrollWheelZoom={true} zoomControl={false} style={{
      height: '100%',
      width: '100%'
    }} data-id="element-1565">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" keepBuffer={8} updateWhenZooming={false} updateWhenIdle={true} data-id="element-1566" />

        <ZoomControl position="topright" data-id="element-1567" />
        <MapResizer data-id="element-1568" />
        <ZoomTracker onZoomChange={handleZoomChange} data-id="element-1569" />
        <MapMovementTracker onMovingChange={setIsMapMoving} data-id="element-1570" />
        <TilePreloader packages={packages} data-id="element-1571" />
        <MapController center={center} zoom={targetZoom} activePackage={activePackage || null} data-id="element-1572" />
        <TooltipPositionUpdater hoveredPackage={hoveredPackage} hoveredEmergency={hoveredEmergency} hoveredProperty={hoveredProperty?.property || null} onPositionUpdate={setTooltipPos} data-id="element-1573" />

        {onEmergencyPositionUpdate && <SelectedEmergencyTracker selectedEmergencyId={selectedEmergencyId || null} emergencyInterventions={emergencyInterventions} onPositionUpdate={onEmergencyPositionUpdate} data-id="element-1574" />}

        {/* === HQ MARKER === */}
        <Marker position={[41.8986, 12.4769]} icon={createHeadquarterIcon()} interactive={false} // Static, no events
      data-id="element-1575" />

        {/* === EMERGENCY MARKERS === */}
        {!isEditing && emergencyInterventions.map(intervention => <Marker key={`emergency-${intervention.id}`} position={[intervention.lat, intervention.lng]} icon={createEmergencyIcon()} eventHandlers={{
        mouseover: () => handleEmergencyMouseOver(intervention),
        mouseout: handleEmergencyMouseOut,
        click: e => {
          // Stop propagation to prevent map click handler from firing immediately
          L.DomEvent.stopPropagation(e.originalEvent);
          // Clear hover tooltip when clicking
          setHoveredEmergency(null);
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
          onEmergencyClick?.(intervention.id);
        }
      }} data-id="element-1576" />)}

        {/* === NORMAL MODE (not editing) === */}
        {!isEditing && packages.map(pkg => {
        const isActive = pkg.id === activePackageId;
        const shouldSplit = isActive && showSplitView;
        if (shouldSplit) {
          // Zoomed in on active package: show area circle + individual property dots
          const radius = computeBoundingCircle(pkg.properties, pkg.lat, pkg.lng);
          return <Fragment key={pkg.id} data-id="element-1577">
                  {/* Teal area circle — hidden during zoom/pan to avoid desync */}
                  {!isMapMoving && <Circle center={[pkg.lat, pkg.lng]} radius={radius} pathOptions={{
              color: '#2E8C7E',
              fillColor: '#2E8C7E',
              fillOpacity: 0.12,
              weight: 2,
              opacity: 0.4
            }} data-id="element-1578" />}
                  {/* Individual property dots */}
                  {pkg.properties.map((prop, idx) => <Marker key={`prop-${prop.id}`} position={[prop.lat, prop.lng]} icon={createPropertyDotIcon(idx + 1)} eventHandlers={{
              click: () => onPackageSelect(pkg.id),
              mouseover: () => {
                if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                setHoveredPackage(null);
                setHoveredEmergency(null);
                hoverTimeoutRef.current = setTimeout(() => {
                  setHoveredProperty({
                    property: prop,
                    index: idx + 1
                  });
                }, 100);
              },
              mouseout: () => {
                if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                setHoveredProperty(null);
              }
            }} data-id="element-1579" />)}
                </Fragment>;
        }
        // Zoomed out or inactive: show single zone dot
        return <Marker key={pkg.id} position={[pkg.lat, pkg.lng]} icon={createZoneIcon(pkg.count, isActive)} eventHandlers={{
          click: () => onPackageSelect(pkg.id),
          mouseover: () => handleMarkerMouseOver(pkg),
          mouseout: handleMarkerMouseOut
        }} data-id="element-1580" />;
      })}

        {/* === EDIT MODE === */}
        {isEditing && activePackage && <>
            {/* Search radius circle — hidden during zoom/pan */}
            {!isMapMoving && <Circle center={[activePackage.lat, activePackage.lng]} radius={800} pathOptions={{
          color: '#2E8C7E',
          fillColor: '#2E8C7E',
          fillOpacity: 0.05,
          dashArray: '5, 10',
          weight: 1
        }} data-id="element-1581" />}

            {/* Included properties with remove badge */}
            {editedProperties.map(prop => <Marker key={`inc-${prop.id}`} position={[prop.lat, prop.lng]} icon={createEditPropertyIcon('included')} eventHandlers={{
          click: () => onToggleProperty?.(prop)
        }} data-id="element-1582" />)}

            {/* Nearby available properties with add badge */}
            {nearbyProperties.map(prop => <Marker key={`avl-${prop.id}`} position={[prop.lat, prop.lng]} icon={createEditPropertyIcon('available')} eventHandlers={{
          click: () => onToggleProperty?.(prop)
        }} data-id="element-1583" />)}
          </>}
      </MapContainer>

      {/* Tooltips rendered outside MapContainer but inside relative wrapper */}
      {hoveredPackage && <PackageHoverTooltip pkg={hoveredPackage} position={tooltipPos} visible={!!hoveredPackage} data-id="element-1584" />}
      {hoveredEmergency && <EmergencyHoverTooltip intervention={hoveredEmergency} position={tooltipPos} visible={!!hoveredEmergency} data-id="element-1585" />}
      {hoveredProperty && <PropertyHoverTooltip property={hoveredProperty.property} index={hoveredProperty.index} position={tooltipPos} visible={!!hoveredProperty} data-id="element-1586" />}
    </div>;
}